import React from 'react';
import type { Language } from '@/lib/i18n';
import { t } from '@/lib/i18n';
import { Lightbulb } from 'lucide-react';

interface ExampleStepData {
  type: 'example';
  title: { fr: string; ru: string };
  content: { fr: string; ru: string };
  noteToRememberFr?: string;
  noteToRememberRu?: string;
}

interface Props {
  step: ExampleStepData;
  language: Language;
}

export default function ExampleStep({ step, language }: Props) {
  const content = step.content[language];

  const renderContent = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let tableRows: string[] = [];
    let inTable = false;

    lines.forEach((line, i) => {
      if (line.startsWith('|')) {
        inTable = true;
        tableRows.push(line);
      } else {
        if (inTable && tableRows.length > 0) {
          // Render table
          const headers = tableRows[0].split('|').filter((c) => c.trim()).map((c) => c.trim());
          const dataRows = tableRows.slice(2).map((row) =>
            row.split('|').filter((c) => c.trim()).map((c) => c.trim())
          );
          elements.push(
            <div key={`table-${i}`} className="overflow-x-auto my-3">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-primary/10">
                    {headers.map((h, hi) => (
                      <th key={`th-${hi}`} className="px-4 py-2 text-left font-600 text-primary border border-primary/20 text-xs">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataRows.map((row, ri) => (
                    <tr key={`tr-${ri}`} className={ri % 2 === 0 ? 'bg-card' : 'bg-muted/20'}>
                      {row.map((cell, ci) => (
                        <td key={`td-${ri}-${ci}`} className="px-4 py-2 border border-border text-foreground text-sm">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          tableRows = [];
          inTable = false;
        }

        if (line.trim()) {
          const parts = line.split(/\*\*(.*?)\*\*/g);
          elements.push(
            <p key={`line-${i}`} className="text-sm text-foreground leading-relaxed mb-2">
              {parts.map((part, j) =>
                j % 2 === 1 ? (
                  <strong key={`bold-${i}-${j}`} className="font-700 text-foreground">
                    {part}
                  </strong>
                ) : (
                  part
                )
              )}
            </p>
          );
        }
      }
    });

    return elements;
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
          <Lightbulb size={20} className="text-accent" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground font-500">{t(language, 'lesson_example')}</p>
          <h2 className="text-lg font-700 text-foreground">{step.title[language]}</h2>
        </div>
      </div>

      <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-5">
        {renderContent(content)}
      </div>

      <div className="mt-4 bg-secondary rounded-xl p-4 border border-border">
        <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide mb-1">
          {language === 'fr' ? '📝 À retenir' : '📝 Запомните'}
        </p>
        <p className="text-sm text-foreground font-500">
          {language === 'fr' ? (step.noteToRememberFr ?? "L'équation comptable Actif = Passif + Capitaux propres doit toujours rester équilibrée.")
            : (step.noteToRememberRu ?? 'Бухгалтерское уравнение Актив = Пассив + Собственный капитал всегда должно оставаться сбалансированным.')}
        </p>
      </div>
    </div>
  );
}